import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { fetchClient } from "../../API/fetchClient";
import type { DimensionPoste } from "../../types/dimension";

interface UseGetPosteDimensionsOptions
  extends Omit<UseQueryOptions<DimensionPoste[], Error>, "queryKey" | "queryFn"> {
  posteId: number;
}

/**
 * Hook to fetch Dimensions for a specific poste
 * GET /iam/postes-emission/{posteId}/dimensions/
 */
export const useGetPosteDimensions = ({
  posteId,
  ...queryOptions
}: UseGetPosteDimensionsOptions) => {
  return useQuery<DimensionPoste[], Error>({
    queryKey: ["posteDimensions", posteId],
    queryFn: async () => {
      console.log(`[useGetPosteDimensions] Fetching dimensions for poste ID: ${posteId}`);

      const response = await fetchClient<DimensionPoste[]>(
        `/iam/postes-emission/${posteId}/dimensions/`
      );

      if (response.error) {
        console.error(`[useGetPosteDimensions] API Error:`, response.error);

        if (response.error.detail) {
          throw new Error(response.error.detail);
        }

        if (response.error.non_field_errors && response.error.non_field_errors.length > 0) {
          throw new Error(response.error.non_field_errors.join(", "));
        }

        // Handle 404
        if (response.status === 404) {
          console.warn(`[useGetPosteDimensions] Poste ${posteId} not found, returning empty array`);
          return [];
        }

        throw new Error(
          `Failed to fetch dimensions for poste ${posteId}: ${JSON.stringify(response.error)}`
        );
      }

      if (!response.data) {
        console.warn(`[useGetPosteDimensions] No data received for poste ${posteId}`);
        return [];
      }

      if (!Array.isArray(response.data)) {
        console.error(
          `[useGetPosteDimensions] Invalid data format - expected array, got:`,
          typeof response.data
        );
        throw new Error(`Invalid data format received for poste ${posteId}`);
      }

      console.log(
        `[useGetPosteDimensions] Successfully fetched ${response.data.length} dimensions for poste ${posteId}`
      );
      return response.data;
    },
    enabled: !!posteId,
    staleTime: 1000 * 60 * 5,
    retry: (failureCount, error) => {
      if (error.message.includes("404")) {
        return false;
      }
      return failureCount < 2;
    },
    ...queryOptions,
  });
};
